import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { fmtDateTimeGB } from "../../lib/format";
import { toast } from "sonner";
import { Clock, GitCommitVertical, MessageSquare, User as UserIcon, Loader2 } from "lucide-react";

// Stage-key → label matches backend PM_STATUS_LABELS
const STAGE_LABELS = {
  solar_request: "Solar Request",
  pre_design: "Pre-Design",
  solar_design: "Solar Design",
  customer_approval: "Customer Approval",
  asset_manager_approval: "Asset Manager Approval",
  capex_submission: "CAPEX Submission",
  capex_approval: "CAPEX Approval",
  contracts: "Contracts",
  construction: "Construction",
  final_handover: "Final Handover",
};

function fmtDaysFriendly(d) {
  if (d == null) return "—";
  if (d < 1) return `${Math.round(d * 24)}h`;
  if (d < 14) return `${d.toFixed(1)} days`;
  if (d < 60) return `${Math.round(d)} days`;
  return `${(d / 30).toFixed(1)} months`;
}

/**
 * Lifecycle timeline — chronological reveal of every stage transition
 * with the actor, the note they left, and how long the previous stage
 * took. Rendered as a vertical connected timeline for scannability.
 */
export default function LifecycleTimeline({ projectId }) {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/lifecycle-history`);
        if (!cancel) setState({ loading: false, data });
      } catch (e) {
        if (!cancel) {
          toast.error(formatApiError(e.response?.data?.detail) || "Could not load lifecycle history");
          setState({ loading: false, data: null });
        }
      }
    })();
    return () => { cancel = true; };
  }, [projectId]);

  if (state.loading) {
    return (
      <div className="flex items-center justify-center py-16 muted text-sm gap-2" data-testid="pm-timeline-loading">
        <Loader2 size={16} className="animate-spin" /> Loading lifecycle history…
      </div>
    );
  }

  if (!state.data) return null;

  const history = state.data.history || [];
  const currentStage = state.data.lifecycle_status || "solar_request";
  const currentDays = state.data.current_stage_days;

  return (
    <div className="max-w-3xl" data-testid="pm-timeline-tab">
      <div className="mb-6">
        <div className="label mb-1">Project Management</div>
        <h2 className="font-display text-2xl font-bold">Lifecycle Timeline</h2>
        <p className="muted text-sm mt-1">
          Every stage transition on this project, with actor, note and duration spent in the previous stage.
        </p>
      </div>

      {/* Current stage badge */}
      <div
        className="mb-6 p-4 rounded-lg border border-[var(--line)] bg-[var(--surface-2,#F6F7F9)] flex items-center justify-between gap-4 flex-wrap"
        data-testid="pm-timeline-current"
      >
        <div>
          <div className="label !text-[9px]">Current stage</div>
          <div className="font-display text-lg font-bold mt-1">{STAGE_LABELS[currentStage] || currentStage}</div>
        </div>
        <div className="text-right">
          <div className="label !text-[9px]">In stage for</div>
          <div className="font-display text-lg font-bold mt-1 inline-flex items-center gap-1.5" style={{ color: "var(--accent-hover)" }}>
            <Clock size={14} /> {fmtDaysFriendly(currentDays)}
          </div>
        </div>
      </div>

      {history.length === 0 && (
        <div className="p-6 text-center muted text-sm italic border border-dashed border-[var(--line)] rounded-lg" data-testid="pm-timeline-empty">
          No stage transitions yet. This project is still on its first stage.
        </div>
      )}

      {history.length > 0 && (
        <ol className="relative border-l-2 border-[var(--line)] ml-3" data-testid="pm-timeline-list">
          {history.map((h, i) => (
            <li key={i} className="pl-6 pb-6 relative" data-testid={`pm-timeline-entry-${i}`}>
              {/* Dot */}
              <span
                className="absolute -left-[9px] top-0 w-4 h-4 rounded-full border-2 border-white shadow-sm"
                style={{ background: "var(--accent-hover)" }}
              >
                <GitCommitVertical size={10} className="absolute inset-0 m-auto text-white" />
              </span>

              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="tag" data-testid={`pm-timeline-from-${i}`}>{STAGE_LABELS[h.from] || h.from}</span>
                <span className="muted text-xs">→</span>
                <span className="tag ok" data-testid={`pm-timeline-to-${i}`}>{STAGE_LABELS[h.to] || h.to}</span>
                {h.duration_days != null && (
                  <span
                    className="text-[11px] muted font-mono inline-flex items-center gap-1"
                    title={`Spent in ${STAGE_LABELS[h.from] || h.from} for ~${fmtDaysFriendly(h.duration_days)}`}
                    data-testid={`pm-timeline-duration-${i}`}
                  >
                    <Clock size={10} /> {fmtDaysFriendly(h.duration_days)}
                  </span>
                )}
              </div>

              <div className="text-xs muted mb-1">{fmtDateTimeGB(h.at)}</div>

              <div className="text-xs inline-flex items-center gap-1.5 mb-2">
                <UserIcon size={11} className="muted" />
                <span className="font-medium">{h.by_user_name || h.by_user_email || "—"}</span>
              </div>

              {h.note && (
                <div
                  className="text-xs bg-white border-l-2 border-[var(--accent)] p-2.5 mt-1 rounded-r flex gap-2 items-start"
                  data-testid={`pm-timeline-note-${i}`}
                >
                  <MessageSquare size={11} className="muted mt-0.5 shrink-0" />
                  <div className="leading-relaxed whitespace-pre-wrap">{h.note}</div>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
